import React, { useContext, useEffect, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/*******INTERNAL IMPORTS********/
import { DisplaySpinnerContext } from "../../../context/ContextIndex";
import {
  DataGridTable,
  GridAction,
  ConfirmationPopup,
  notifyToast,
  ToastType,
  FormPage
} from "../../../components-old/ComponentsIndex";
import { PricingPortalAPI } from "../../../apis/api-index";
import { MdDelete, MdCreate } from "react-icons/md";
import AddIcon from "@mui/icons-material/Add";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import { useSelector } from "react-redux";
import { selectSessionConfig } from "../../../store/store-selector";
import { AppUtility } from "../../../utils/utility";
import { saveDeviceType } from "../api-handler/api-handler";

const DeviceTypeListForm = () => {
  /**************************************************
  //Form handlers & hooks
  **************************************************/
  const { t } = useTranslation();
  const navigate = useNavigate();
  const spinnerCtx = useContext(DisplaySpinnerContext);
  const settingsConfig = useSelector(selectSessionConfig);

  /**************************************************
  //Form state variables
  **************************************************/
  const [deviceTypeRecordList, setDeviceTypeRecordList] = useState([]);
  const [dataGridPageSize, setDataGridPageSize] = useState(10);
  const [totalDeviceType, setTotalDeviceType] = useState(0);
  const [gridPageSizeOptions /*setGridPageSizeOptions*/] = useState([10, 20, 50]);
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);


  /**************************************************
  //Event definitions
  **************************************************/

  //on click of add icon for creating device type
  const handleCreateDeviceTypeClick = () => {
    navigate("/deviceType");
  };

  //onclick of edit device type
  const handleEditDeviceTypeClick = async (param) => {
    let _navigationData = param.recordResponse;
    delete _navigationData.CreatedDate;
    delete _navigationData.ModifiedDate;
    delete _navigationData.CountryCode;
    delete _navigationData.BusinessCode;

    navigate(
      {
        pathname: "/deviceType",
        search: createSearchParams({
          devicetypeid: param.recordResponse.DeviceTypeId,
        }).toString(),
      },
      { state: _navigationData }
    );
  };

  //onclick of confirm of delete popup
  const handleConfirm = async () => {
    let _filterClause = `&devicetypeid=${selectedRow.id}`;
    await fetchAvailableDevice(_filterClause, selectedRow);
    setPopupOpen(false);
    setSelectedRow(null);
  };

  //on click of cancel of delete popup
  const handleCancel = () => {
    setSelectedRow(null);
    setPopupOpen(false);
  };

  //onClick of delete in list
  const showDeleteDeviceTypePopup = (event) => {
    try {
      setSelectedRow(event);
      setPopupOpen(true);
    } catch (err) {
      alert(err);
    }
  };

  //on page change
  const handlePageSizeChange = (newPageSize) => {
    setDataGridPageSize(newPageSize);
  };

  //show review modal
  const handleShowSelectedDeviceTypeChanges = (event) => {
    let _navigationData = { previousPath: "/devicetypelist" };
    navigate(
      {
        pathname: "/reviewdetails",
        search: createSearchParams({
          reviewFilter: `&locationkey=${event.id}`,
        }).toString(),
      },
      { state: _navigationData }
    );
  };

  /**************************************************
  //Fetch API & utility
  **************************************************/


  const setDeviceTypeList = (deviceTypeList) => {
    try {
      deviceTypeList.sort((a, b) => new Date(b.ModifiedDate).getTime() - new Date(a.ModifiedDate).getTime());
      let _deviceTypeList = deviceTypeList.map((item) => {
        return {
          id: item.DeviceTypeId,
          typeName: item.TypeName,
          longName: item.LongName,
          recordResponse: item
        };
      });

      setDeviceTypeRecordList(_deviceTypeList);
      setTotalDeviceType(_deviceTypeList.length);
      spinnerCtx.setLoadingIndicator(false);
    } catch (err) {
      spinnerCtx.setLoadingIndicator(false);
      alert(err);
    }
  };

  const FetchDeviceTypeList = async () => {
    try {
      let _lists = await PricingPortalAPI.getList(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getDeviceType",
        "devicetype"
      );
      setDeviceTypeList(_lists);
    } catch (err) {
      alert(err);
    }
  };
  //fetch available device before deleting service type
  const fetchAvailableDevice = async (filter, param) => {
    try {
      let _availableDevice = await PricingPortalAPI.getFilterList(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getDeviceList",
        "device",
        filter
      );

      if (_availableDevice && _availableDevice.length > 0) {
        notifyToast("Device combination exists with this service type", ToastType.ERROR);
      } else {
        await fetchAvailableItineraryTimes(filter, param);
      }
    } catch (e) {
      alert(e);
    }
  };

  //fetch gross branch before deleting service type
  const fetchAvailableItineraryTimes = async (filter, param) => {
    try {
      let _availableItineraryTimes = await PricingPortalAPI.getFilterList(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getItineraryTimesList",
        "itinerarytimes",
        filter
      );

      if (_availableItineraryTimes && _availableItineraryTimes.length > 0) {
        notifyToast("Itinerary time combination exists with this device type", ToastType.ERROR);
      } else {
        await deleteDeviceType(param);
      }
    } catch (e) {
      alert(e);
    }
  };

  const formPreviousDataForDelete = (param) => {
    try {
      let _previousState = param.recordResponse;
      _previousState.Deleted = true;
      delete _previousState.CreatedDate;
      delete _previousState.ModifiedDate;
      delete _previousState.CountryCode;
      delete _previousState.BusinessCode;
      delete _previousState.Version;
      delete _previousState.DeviceTypeId;
      delete _previousState.Compatible;
      delete _previousState.Myrentokil;
      delete _previousState.Technical;
      delete _previousState.Status;
      return _previousState ? _previousState : null;
    } catch (e) {
      alert(e);
    }
  };

  //Delete API call
  const deleteDeviceType = async (param) => {
    try {
      spinnerCtx.setLoadingIndicator(true);
      let EditDeviceTypeId = param.id;
      const status = await saveDeviceType(formPreviousDataForDelete(param), {
        EditDeviceTypeId,
        settingsConfig
      });

      if (status?.responseStatus === 200) {
        notifyToast("Sent for Approval", ToastType.SUCCESS);
        await FetchDeviceTypeList();
      } else {
        notifyToast(status.errorMessage ? status.errorMessage : "Device type deletion failed", ToastType.ERROR);
        spinnerCtx.setLoadingIndicator(false);
      }
    } catch (err) {
      alert(err);
    }
  };

  /**************************************************
  //JSX delcaration
  **************************************************/

  //data grid schema
  const dataGridSchema = [
    {
      field: "typeName",
      headerName: t("deviceTypeList.typeName"),
      flex: 2,
    },
    {
      field: "longName",
      headerName: t("deviceTypeList.longName"),
      flex: 3,
    },
    {
      field: "recordResponse",
      hide: true,
    },
    {
      field: "action",
      headerName: t,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return (
          <GridAction actionsConfig={actionConfigs} row={params.row} />
        );
      },
    },
  ];

  //button configs for add and download list
  const buttonConfigs = [
    {
      buttonText: <AddIcon />,
      buttonType: "filled",
      onClick: handleCreateDeviceTypeClick,
      buttonColor: "#A62B23",
      textColor: "#fff",
      action: "create"
    },
    {
      buttonText: <SystemUpdateAltIcon />,
      buttonType: "outlined",
      onClick: () => {
        AppUtility.handleDownload(deviceTypeRecordList, "devicetypelist.xlsx");
      },
      buttonColor: "#A62B23",
      textColor: "#A62B23",
      action: "download"
    },
  ];

  //action configs for edit and delete record
  const actionConfigs = [
    {
      onClick: handleShowSelectedDeviceTypeChanges,
      action: "review"
    },
    {
      icon: MdCreate,
      onClick: handleEditDeviceTypeClick,
      buttonColor: "#EEEEE",
      action: "edit"
    },
    {
      icon: MdDelete,
      onClick: showDeleteDeviceTypePopup,
      buttonColor: "#A62B23",
      action: "delete"
    },
  ];


  /**************************************************
  //Form on load useEffect
  **************************************************/
  useEffect(() => {

    spinnerCtx.setLoadingIndicator(true);
    FetchDeviceTypeList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormPage isList={true}>
      <DataGridTable
        rows={deviceTypeRecordList}
        columns={dataGridSchema}
        pageSize={dataGridPageSize}
        pageSizeOptions={gridPageSizeOptions}
        onPageSizeChange={handlePageSizeChange}
        totalRowCount={totalDeviceType}
        toolbarHeader={t("deviceTypeList.header")}
        toolbarFilterPlaceholder={t("quoteApproval.toolbarFilterPlaceholder")}
        buttonConfigs={buttonConfigs}
      />
      <ConfirmationPopup
        isOpen={popupOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        confirmationText={t("popup.deleteDeviceTypeConfirmationMessage")}
        eventObj={selectedRow}
      />
    </FormPage>
  );
};

export default DeviceTypeListForm;
